const COMPANY = 'Uppbeat';
const MIDDOT = '•';
const DASH = '-';
const SEPERATOR = ` ${MIDDOT} `;
const PAGE_SEPERATOR = ' ';
const SEO_TITLE_FORMAT =
  '{title}{page}{seperator}{tagline}{seperator}{company}';

const debugTitle = (...args: unknown[]) => {
  if (process.env.NODE_ENV === 'development') {
    // biome-ignore lint/suspicious/noConsole: Intentional dev-only debug helper
    console.log('[SEO]', ...args);
  }
};

interface GenerateTitleProps {
  excludeTagline?: boolean;
  page?: number;
  tagline?: string | false;
  title: string;
}

const generateTitle = ({
  title,
  page,
  tagline,
  excludeTagline = false,
}: GenerateTitleProps) => {
  const _tagline =
    tagline ?? (excludeTagline ? undefined : 'Free Music For Creators');

  const generatedTitle = SEO_TITLE_FORMAT.replace('{title}', title)
    .replace(
      '{page}',
      page && page > 1 ? ` (page ${PAGE_SEPERATOR}${page})` : ''
    )
    .replace(
      _tagline ? '{tagline}' : '{seperator}{tagline}',
      _tagline ? `${_tagline}` : ''
    )
    .replace('{company}', `${COMPANY}`)
    .replaceAll('{seperator}', SEPERATOR);

  debugTitle('generatedTitle', generatedTitle);

  return generatedTitle;
};

const parseExistingTitle = (
  existingTitle: string,
  options?: { lowercase?: boolean }
) => {
  const [titleWithPossiblyPageNumber, tagline] = existingTitle.split(MIDDOT);
  const [title, pageNumber] = titleWithPossiblyPageNumber.split(`${DASH} Page`);

  let processedTitle = title.trim();

  // If lowercase option is enabled, preserve 'Free' but lowercase the rest
  if (options?.lowercase && processedTitle.startsWith('Free ')) {
    const parts = processedTitle.split('Free ');

    if (parts.length > 1) {
      processedTitle = `Free ${parts[1].toLowerCase()}`;
    }
  } else if (options?.lowercase) {
    processedTitle = processedTitle.toLowerCase();
  }

  return {
    title: processedTitle,
    tagline: tagline && tagline.trim() !== COMPANY ? tagline.trim() : undefined,
    pageNumber: pageNumber ? Number(pageNumber.trim()) : undefined,
  };
};

const useSEO = () => {
  const generateNewTitle = ({
    title,
    page,
    tagline,
    excludeTagline,
  }: GenerateTitleProps) =>
    generateTitle({ title, page, tagline, excludeTagline });

  const generateTitleFromExistingTitle = ({
    existingTitle,
    fallbackTitle,
    pageNumber,
    excludeTagline,
    lowercase,
  }: {
    existingTitle?: string;
    fallbackTitle: string;
    pageNumber?: number;
    excludeTagline?: boolean;
    lowercase?: boolean;
  }) => {
    debugTitle('existingTitle', existingTitle);
    debugTitle('fallbackTitle', fallbackTitle);
    debugTitle('pageNumber', pageNumber);

    const {
      title,
      tagline,
      pageNumber: parsedPageNumber,
    } = parseExistingTitle(existingTitle ?? fallbackTitle, { lowercase });

    return generateNewTitle({
      title,
      page: pageNumber ?? parsedPageNumber,
      tagline,
      excludeTagline,
    });
  };

  return {
    generateNewTitle,
    generateTitleFromExistingTitle,
  };
};

export default useSEO;
